<template>
  <div
    class="file-input"
    :class="{
      error: vErrors.length > 0 || isError,
    }"
    v-click-outside="emitFocusOut"
  >
    <div class="file-input__wrapper">
      <div class="file-input__title">
        {{ translatedTitle }}
      </div>
      <label class="file-input__el">
        <input
          type="file"
          ref="inputEl"
          :accept="accept"
          @change="processInput"
        />
        <span
          class="file-input__el-text"
          :title="selectedFile ? selectedFile.name : translatedPlaceholder"
          :class="{ 'empty-file': !selectedFile }"
        >
          {{ selectedFile ? selectedFile.name : translatedPlaceholder }}
        </span>
        <span class="file-input__el-icon">
          <img :src="$options.FILE_INPUT_ICON" />
        </span>
      </label>
    </div>

    <div class="file-input__hint" v-if="hasHint">
      {{ translatedHint }}
    </div>
    <div class="file-input__errors" v-if="vErrors.length > 0">
      <div class="file-input__error">{{ vErrors[0].$message }}</div>
    </div>
  </div>
</template>

<script>
import FileInputIcon from '@js-app/assets/base/file-input-icon.svg'
import {$emit} from "../../../utils/gogocodeTransfer.js";

export default {
  name: 'FileInput',
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    title: {
      type: [String, Number, Object],
      default: '',
    },
    placeholder: {
      type: [String, Object],
      default: '',
    },
    value: [File],
    hint: {
      type: [String, Object, Array],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    vErrors: {
      type: Array,
      default() {
        return []
      },
    },
    isError: {
      type: Boolean,
      default: false,
    },
    shouldTranslateTitle: {
      type: Boolean,
      default: false,
    },
    localizedTitle: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: '',
    },
    gate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      file: null,
      isFocused: false,
      currentType: '',
      selectedFile: null,
    }
  },
  FILE_INPUT_ICON: FileInputIcon,
  computed: {
    locale() {
      let lang = this.$i18n?.locale ?? 'en'
      return lang === 'ua' ? 'uk' : lang
    },
    translatedTitle() {
      if (this.shouldTranslateTitle) {
        return this.$t(this.title)
      }

      if (this.localizedTitle && typeof this.title === 'object') {
        return this.title[this.locale] ?? this.title['en'] ?? ''
      }

      return this.title
    },
    translatedPlaceholder() {
      if (this.shouldTranslateTitle) {
        return this.$t(this.placeholder)
      }

      if (this.localizedTitle && typeof this.placeholder === 'object') {
        return this.placeholder[this.locale] ?? this.placeholder['en'] ?? ''
      }

      return this.placeholder
    },
    translatedHint() {
      if (this.shouldTranslateTitle) {
        return this.$t(this.hint)
      }

      if (this.localizedTitle && typeof this.hint === 'object') {
        return this.hint[this.locale] ?? this.hint['en'] ?? ''
      }

      return this.hint
    },
    hasHint() {
      if (Array.isArray(this.hint)) {
        return this.hint.some(
          (item) => item != null && String(item).trim() !== ''
        )
      }

      return this.hint != null && String(this.hint).trim() !== ''
    },
  },
  watch: {
    file() {
      this.$emit('update:value', this.file)
    },
  },
  methods: {
    processFocus() {
			this.$emit('focus')
			this.$emit('textInputFocus')
      this.isFocused = true
    },
    processBlur() {
			this.$emit('textInputBlur')
      this.isFocused = false
    },

    processInput(e) {
      const files = e.target.files || e.dataTransfer.files

      if (files.length > 0) {
        const originalFile = files[0]

        if (this.gate) {
          const upperCaseFileName = originalFile.name.toLowerCase()
          this.selectedFile = new File([originalFile], upperCaseFileName, {
            type: originalFile.type,
            lastModified: originalFile.lastModified,
          })
        } else {
          this.selectedFile = originalFile
        }

				this.$emit('update:value', this.selectedFile)
      }
    },
    copyToClipboard() {
      if (!navigator.clipboard) {
        return
      }

      navigator.clipboard.writeText(this.text).then(
        () => {
          this.$emit('copied')
        },
        (err) => {
          console.error('Async: Could not copy text: ', err)
        }
      )
    },
    focus() {
      this.$refs.inputEl.focus()
    },
    togglePasswordShow() {
      this.currentType = this.currentType === 'password' ? 'text' : 'password'
    },
    emitFocus() {
      $emit(this, 'focus')
    },
    emitFocusOut() {
      $emit(this, 'focusout')
    },
  },
  emits: [
    'update',
    'focus',
    'textInputFocus',
    'textInputBlur',
    'copied',
    'focusout',
    'update:value',
  ],
}
</script>

<style lang="scss" scoped>
.file-input__title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}
.password-show {
  position: absolute;
  top: 50%;
  right: 18px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
}
</style>

<style>
:-webkit-autofill {
  animation-name: onAutoFillStart;
}
:not(:-webkit-autofill) {
  animation-name: onAutoFillCancel;
}
@keyframes onAutoFillStart {
  from {
  }
  to {
  }
}
@keyframes onAutoFillCancel {
  from {
  }
  to {
  }
}
</style>
